import type { FC, ReactNode } from 'react'
import type { Article } from '@/types'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import { readingTime } from '@/lib/reading-time'

interface ArticleRowProps {
    article: Article
    index?: number
    className?: string
}

/* A studio's writing should look like writing. No cover photo: the row IS the
   design. Rule, number, category, title, then the meta pushed to the far edge,
   with an arrow that slides in on hover to carry the interaction. */
export const ArticleRow: FC<ArticleRowProps> = ({ article, index, className }): ReactNode => {
    return (
        <li
            data-journal-row
            data-preview-src={article.image}
            className={cn('group/row relative', className)}
        >
            <span
                data-journal-rule
                aria-hidden='true'
                className='block h-px w-full origin-left bg-current opacity-15'
            />

            <Link
                href={`/journal/${article.slug}`}
                data-cursor='view'
                data-cursor-label='Read'
                className='grid grid-cols-[1fr_auto] items-baseline gap-x-5 gap-y-3 py-6 transition-[padding] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] lg:grid-cols-[auto_1fr_auto_auto] lg:gap-x-8 lg:py-8 lg:group-hover/row:pl-4'
            >
                {typeof index === 'number'
                    ? (
                        <span className='t-mono hidden self-start pt-[0.5em] text-ash transition-colors duration-500 group-hover/row:text-brand lg:block'>
                            {String(index + 1).padStart(2, '0')}
                        </span>
                    )
                    : null}

                <span className='flex min-w-0 flex-col gap-2'>
                    <span className='t-mono text-ash'>
                        {article.category}
                    </span>

                    <h2 className='t-display text-[clamp(23px,3.2vw,44px)] leading-[1.02] transition-colors duration-500 group-hover/row:text-brand'>
                        {article.title}
                    </h2>
                </span>

                <span className='flex flex-col items-end gap-2 lg:flex-row lg:items-baseline lg:gap-8'>
                    <time dateTime={article.datetime} className='t-mono text-ash'>
                        {article.date}
                    </time>

                    <span className='t-mono text-ash'>
                        {readingTime(article.body)}
                    </span>
                </span>

                <span
                    aria-hidden='true'
                    className='hidden self-center text-brand opacity-0 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] -translate-x-3 group-hover/row:translate-x-0 group-hover/row:opacity-100 lg:block'
                >
                    <svg viewBox='0 0 24 24' className='size-5' fill='none'>
                        <path d='M5 19L19 5M19 5H8M19 5v11' stroke='currentColor' strokeWidth='1.8' />
                    </svg>
                </span>
            </Link>
        </li>
    )
}
